// a library to wrap and simplify api calls
import apisauce from 'apisauce'
import AppConfig from '../Config/AppConfig'
import ObjectCleaner from '../Utils/ObjectCleaner'
import Cookie from 'js-cookie'
import { currentCurrency } from '../Utils/PrivilegeUtils'
import axios from 'axios'
import moment from 'moment'

const apiURL = AppConfig.apiURL

// our "constructor"
const create = (baseURL = apiURL) => {
  // ------
  // STEP 1
  // ------
  //
  // Create and configure an apisauce-based api object.
  //
  const apiWrapper = apisauce.create({
    // base URL is read from the "constructor"
    baseURL: apiURL,
    // 10 second timeout... | extended timeout to 1 minute
    timeout: 60000
  })

  const headerWithToken = (Authorization) => {
    return {
      headers: {
        Authorization
      }
    }
  }

  // Wrap api's addMonitor to allow the calling code to attach
  // additional monitors in the future.  But only in __DEV__ and only
  // if we've attached Reactotron to console (it isn't during unit tests).
  if (AppConfig.NODE_ENV === 'development') {
    const naviMonitor = (response) => console.log('DEBUG RESPONSE:', response)
    apiWrapper.addMonitor(naviMonitor)
  }

  // ------
  // STEP 2
  // ------
  //
  // Define some functions that call the api.  The goal is to provide
  // a thin wrapper of the api layer providing nicer feeling functions
  // rather than "get", "post" and friends.
  //
  // I generally don't like wrapping the output at this level because
  // sometimes specific actions need to be take on `403` or `401`, etc.
  //
  // Since we can't hide from that, we embrace it by getting out of the
  // way at this level.
  //

  // example get data
  const getUserData = (userId) => {
    return apiWrapper.get(`/users/${userId}`)
  }

  // example get data with token
  const getDatawithToken = (token) => {
    return apiWrapper.get('/url/', {}, headerWithToken(token))
  }

  // example post data
  const postData = (data) => {
    return apiWrapper.post('/url/action', { data })
  }

  // PRESET
  const getCurrencies = () => {
    return apiWrapper.get('/presets/currencies')
  }

  const getLanguages = () => {
    return apiWrapper.get('/presets/languages')
  }

  const getCountries = () => {
    return apiWrapper.get('/locations/countries')
  }

  const getPresetOrder = () => {
    let filter = { currency_code: currentCurrency() }
    return apiWrapper.get(`/presets/orders`, ObjectCleaner(filter))
  }

  const updatePresetOrder = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put('/presets/orders', data, headerWithToken(`JWT ${token}`))
  }

  // WIP PRESET BRANCH
  const getBranch = (token) => {
    return apiWrapper.get('/presets/branches', null, headerWithToken(`JWT ${token}`))
  }

  const updateBranch = (token, data) => {
    return apiWrapper.put(`/presets/branches`, data, headerWithToken(`JWT ${token}`))
  }

  // PRESET SHIPPING
  const getShipping = () => {
    return apiWrapper.get('/presets/shippings')
  }

  const updateShipping = (token, data) => {
    return apiWrapper.put('presets/shippings', data, headerWithToken(`JWT ${token}`))
  }

  // AUTH COLLECTIONS
  const login = (data) => {
    return apiWrapper.post('/auth/login',
      data
    )
  }

  const checkLogin = (token) => {
    return apiWrapper.get('/users/me', {}, headerWithToken(`JWT ${token}`))
  }

  // DESTINATION COLLECTIONS
  const getDestination = (token) => {
    return apiWrapper.get('/destinations', {}, headerWithToken(`JWT ${token}`))
  }

  const createDestination = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post('/destinations', data, headerWithToken(`JWT ${token}`))
  }

  const deleteDestination = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete('/destinations/' + data.id, {}, headerWithToken(`JWT ${token}`))
  }

  const updateDestination = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put('/destinations/' + data.id, data.content, headerWithToken(`JWT ${token}`))
  }

  const addAttractionToDestination = (token, data) => {
    return apiWrapper.post(`/destinations/${data.destination_id}/attractions`, { attractions_ids: data.attraction_id }, headerWithToken(`JWT ${token}`))
  }

  const deleteAttractionFromDestination = (token, data) => {
    return apiWrapper.delete(`/destinations/${data.destination_id}/attractions/${data.attraction_id}`, null, headerWithToken(`JWT ${token}`))
  }

  const getContinent = (token) => {
    return apiWrapper.get('/locations', null, headerWithToken(`JWT ${token}`))
  }

  // PLACES MAPPING
  const addPlaceToDestination = (token, data) => {
    return apiWrapper.post(`/destinations/${data.destination_id}/places`, { places: data.places }, headerWithToken(`JWT ${token}`))
  }

  const deletePlaceFromDestination = (token, data) => {
    return apiWrapper.delete(`/destinations/${data.destination_id}/places/${data.place_id}/${data.type}`, null, headerWithToken(`JWT ${token}`))
  }

  const getPlacesV2 = (token, { destination, provider, page, q }) => {
    let filter = { provider, page, q }
    return apiWrapper.get(`/places/destinations/${destination}`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getPlacesByDestination = (token, data) => {
    let filter = {
      page: 1,
      limit: null,
      language_code: 'EN',
      currency_code: 'IDR',
      order: 'title',
      // type: null,
      type: 'attraction',
      provider: 'BMG',
      date: null
    }
    if (data) {
      filter = { ...filter, ...data.filter }
    }

    let destination = null
    if (data.destination) {
      Cookie.set('savedDestinationId', data.destination)
      destination = data.destination
    } else {
      destination = Cookie.get('savedDestinationId')
    }
    return apiWrapper.get('/places/destinations/' + destination, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const searchPlaces = (token,
    {
      q = null,
      page = 1,
      limit = 10,
      language_code = 'EN',
      currency_code = 'IDR',
      attraction_type_id = null,
      order = null,
      date = null,
      c = null,
      provider = null
    }
  ) => {
    let params = { q, page, limit, language_code, currency_code, attraction_type_id, order, date, c, provider }
    return apiWrapper.get('/search-places', ObjectCleaner(params), headerWithToken(`JWT ${token}`))
  }

  // ATTRACTION COLLECTIONS
  const getAttractionByDestination = (token, data) => {
    let filter = {
      page: 1,
      limit: null,
      language_code: '',
      currency_code: '',
      // order: 'title',
      date: null
    }
    if (data) {
      filter = { ...filter, ...data.filter }
    }

    let destination = null
    if (data.destination) {
      Cookie.set('savedDestinationId', data.destination)
      destination = data.destination
    } else {
      destination = Cookie.get('savedDestinationId')
    }
    return apiWrapper.get('v2/attractions/destinations/' + destination, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getAttractionDetail = (token, { id, filter }) => {
    return apiWrapper.get(`/attractions/${id}`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getPlaceDetail = (token, data) => {
    let filter = {
      language_code: 'EN',
      currency_code: 'IDR',
      date: null
    }
    if (data) {
      filter = { ...filter, ...data.filter }
    }

    let id = null
    if (data) {
      Cookie.set('getAttractionDetail', data.id)
      id = data.id
    } else {
      id = Cookie.get('getAttractionDetail')
    }
    return apiWrapper.get('/landmarks/' + id, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const updateAttractionDetail = (token,
    {
      id = null,
      form = {}
    }) => {
    let body = {
      is_ignored_on_update: true,
      ...form
    }
    return apiWrapper.put('/attractions/' + id, body, headerWithToken(`JWT ${token}`))
  }

  const searchAttraction = (token,
    {
      q = null,
      page = 1,
      limit = 10,
      language_code = 'EN',
      currency_code = 'IDR',
      attraction_type_id = null,
      order = null,
      date = null,
      c = null,
      provider = null
    }
  ) => {
    let params = { q, page, limit, language_code, currency_code, attraction_type_id, order, date, c, provider }
    return apiWrapper.get('/search', ObjectCleaner(params), headerWithToken(`JWT ${token}`))
  }

  const clearAttractionCache = (token) => {
    return apiWrapper.get('/admin/clearCache', null, headerWithToken(`JWT ${token}`))
  }

  const downloadAttractionData = (token, filter) => {
    filter = { destinationId: null, ...filter }
    let params = []
    if (filter.destinationId) {
      params.push('destinationId=' + filter.destinationId)
    }
    return axios({
      method: 'get',
      url: apiURL + '/attractions/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = 'passpod_bmg_attraction_data.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return {
        ok: true,
        data: 'success',
        message: 'Success downloading attraction data',
        status: true
      }
    })
  }

  // FILES COLLECTIONS
  const uploadFiles = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post('/files', data, headerWithToken(`JWT ${token}`))
  }

  // POPULAR ATTRACTION
  const getPopularAttraction = (token, { modifier = 'origin', modifier_value = 'id' }) => {
    return apiWrapper.get(`v2/attractions/populars/${modifier}/${modifier_value}`, null, headerWithToken(`JWT ${token}`))
  }

  const savePopularAttraction = (token, data) => {
    return apiWrapper.post('/attractions/populars', data, headerWithToken(`JWT ${token}`))
  }

  const deletePopularAttraction = (token, id) => {
    return apiWrapper.delete(`/attractions/populars/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const editPopularAttraction = (token, data) => {
    return apiWrapper.put(`/attractions/populars/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  // COUPON COLLECTIONS
  const getAllCoupon = (token, filter) => {
    filter['show_claimers'] = true
    filter['show_history'] = true
    filter['complete_history'] = true
    return apiWrapper.get('/coupons', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getCouponDetail = (token, data = { id: null, filter: { } }) => {
    let filter = { ...data.filter }
    filter['show_history'] = true
    filter['complete_history'] = true
    return apiWrapper.get(`/coupons/${data.id}`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getAllCouponTag = (token, filter) => {
    filter['show_stats'] = true
    filter['type'] = 'BUSINESS'
    return apiWrapper.get('/coupons/tags', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createNewCoupon = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post('/coupons', data, headerWithToken(`JWT ${token}`))
  }

  const updateCoupon = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/coupons/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const updateBusiness = (token, { tag, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/coupons/tags/${tag}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteCoupon = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/coupons/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const deleteBusiness = (token, { tag, body }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/coupons/tags/${tag}`, body, headerWithToken(`JWT ${token}`))
  }

  const generateCoupon = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/coupons/tools/generate-coupon`, data, headerWithToken(`JWT ${token}`))
  }

  const couponSpreadsheet = (token, data = { type: null, tag: null }) => {
    let params = []
    if (data.type) {
      params.push('type=' + data.type)
    }
    if (data.tag) {
      params.push('tag=' + data.tag)
    }

    return axios({
      method: 'get',
      url: apiURL + '/coupons/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `${data.tag}-${moment().format('YMMDDhhmmss')}-coupon-list.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success getting coupon tags list',
        status: true
      }
    })
  }

  const historySpreadsheet = (token, data = { type: null, tag: null }) => {
    let params = []
    if (data.type) {
      params.push('type=' + data.type)
    }
    if (data.tag) {
      params.push('tag=' + data.tag)
    }

    return axios({
      method: 'get',
      url: apiURL + '/coupons/spreadsheet?' + params.join('&') + '&used=1',
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `${data.tag}-${moment().format('YMMDDhhmmss')}-coupon-history.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success getting coupon history list',
        status: true
      }
    })
  }

  // INDIVIDUAL COUPON COLLECTIONS
  const getCouponItem = (token, data) => {
    return apiWrapper.get(`/coupons/${data.uuid}/items/`, null, headerWithToken(`JWT ${token}`))
  }

  const postCouponItem = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/coupons/${uuid}/items`, form, headerWithToken(`JWT ${token}`))
  }

  const postCouponItemTag = (token, { tag, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/coupons-business/tags/${tag}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteCouponItem = (token, { couponUUID, couponItemId }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/coupons/${couponUUID}/items/${couponItemId}`, null, headerWithToken(`JWT ${token}`))
  }

  const deleteCouponItemTag = (token, { tagName, couponItemId }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/coupons-business/tags/${tagName}/${couponItemId}`, null, headerWithToken(`JWT ${token}`))
  }
  // COUPON PROVIDER COLLECTIONS
  const getCouponProvider = (token, filter) => {
    return apiWrapper.get(`/coupon-providers/`, null, headerWithToken(`JWT ${token}`))
  }

  const postCouponProvider = (token, form) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/coupon-providers/`, form, headerWithToken(`JWT ${token}`))
  }

  const putCouponProvider = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/coupon-providers/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteCouponProvider = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/coupon-providers/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // COUPON PRESET COLLECTIONS
  const getCouponPreset = (token, filter) => {
    return apiWrapper.get(`/coupon-providers/preset`, null, headerWithToken(`JWT ${token}`))
  }

  const postCouponPreset = (token, form) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/coupon-providers/preset`, form, headerWithToken(`JWT ${token}`))
  }

  const putCouponPreset = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/coupon-providers/preset/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteCouponPreset = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/coupon-providers/preset/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // COUPON AFFILIATES (pisces)
  const getAffiliates = (token, filter) => {
    return apiWrapper.get(`/coupons/affiliates`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const affiliateTransfer = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/coupons/affiliates/${uuid}`, form, headerWithToken(`JWT ${token}`))
  }

  // PRODUCT COLLECTIONS
  const getProduct = (token, filter) => {
    filter = {
      // type: null,
      // sort: 'title',
      // currency_code: 'IDR',
      // page: 1,
      // q: null,
      limit: 10,
      ...filter
    }
    return apiWrapper.get('/products', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createProduct = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post('/products', data, headerWithToken(`JWT ${token}`))
  }

  const deleteProduct = (token, data) => {
    return apiWrapper.delete(`/products/${data.id}`, null, headerWithToken(`JWT ${token}`))
  }

  const updateProduct = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/products/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const getProductCountry = (token, data) => {
    return apiWrapper.get(`/products/${data.uuid}/countries`, null, headerWithToken(`JWT ` + token))
  }

  const postProductCountry = (token, data) => {
    apiWrapper.setHeader(`Content-Type`, `application/json`)
    return apiWrapper.post(`/products/set-country`, data, headerWithToken(`JWT ${token}`))
  }

  const deleteProductCountry = (token, { uuid }) => {
    apiWrapper.setHeader(`Content-Type`, `application/json`)
    return apiWrapper.delete(`/products/countries/${uuid}`, null, headerWithToken(`JWT ${token}`))
  }

  const getProductCampaign = (token, { uuid }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/products/${uuid}/campaigns`, null, headerWithToken(`JWT ${token}`))
  }

  const postProductCampaign = (token, { form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/products/campaigns`, form, headerWithToken(`JWT ${token}`))
  }

  const putProductCampaign = (token, { form, variantUUID }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/products/campaigns/${variantUUID}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteProductCampaign = (token, { variantUUID }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/products/campaigns/${variantUUID}`, null, headerWithToken(`JWT ${token}`))
  }

  const getProductVariant = (token, { product_id }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/products/${product_id}/variants`, null, headerWithToken(`JWT ${token}`))
  }

  const postProductVariant = (token, { form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/products/variants`, form, headerWithToken(`JWT ${token}`))
  }

  const putProductVariant = (token, { form, id }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/products/variants/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteProductVariant = (token, { id }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/products/variants/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const getProductVariantItem = (token, { variant_id }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/products/variants/${variant_id}/items`, null, headerWithToken(`JWT ${token}`))
  }

  const postProductVariantItem = (token, { variant_id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/products/variants/${variant_id}/items`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteProductVariantItem = (token, { productvariantitemuuid }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/products/variants/items/${productvariantitemuuid}`, null, headerWithToken(`JWT ${token}`))
  }

  // E-SIM Collecteopn
  const getAllEsims = (token, filter) => {
    filter = { page: 1, limit: 10, ...filter }
    return apiWrapper.get('/esim', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const updateEsimData = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/esim/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }
  
  const updateEsimCountry = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/esim/${data.id}/countries`, data.form, headerWithToken(`JWT ${token}`))
  }

  const updateEsimVariant = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/esim/${data.id}/variants/${data.variant_id}`, data.form, headerWithToken(`JWT ${token}`))
  }
  
  // EVENTS COLLECTIONS
  const getAllEvents = (token, filter) => {
    filter = { page: 1, limit: 10, ...filter }
    return apiWrapper.get('/events', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getEventsCategories = (token, filter) => {
    filter = { page: 1, limit: 12, ...filter }
    return apiWrapper.get('/events/categories', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getAllPopularEvents = (token, filter) => {
    return apiWrapper.get(`/events/popular`, null, headerWithToken(`JWT ${token}`))
  }

  const setPopularEvent = (token, { event_id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/event/popular/${event_id}`, form, headerWithToken(`JWT ${token}`))
  }

  const resetEventDescription = (token, { event_id }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/event/${event_id}?reset=true`, null, headerWithToken(`JWT ${token}`))
  }

  const updateEventDescription = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/event/${data.event_id}`, data, headerWithToken(`JWT ${token}`))
  }

  // INSURANCE COLLECTIONS: TRAVEL PACKAGE
  const getTravelPackage = (token) => {
    return apiWrapper.get(`/travel-packages-admin`, null, headerWithToken(`JWT ${token}`))
  }

  const postTravelPackage = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/travel-packages`, data, headerWithToken(`JWT ${token}`))
  }

  const putTravelPackage = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/travel-packages/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const deleteTravelPackage = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/travel-packages/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  // INSURANCE COLLECTIONS: TRAVEL PACKAGE PRICE
  const viewTravelPackage = (token, data) => {
    return apiWrapper.get(`/travel-packages-admin/${data.id}`, null, headerWithToken(`JWT ${token}`))
  }

  const putTravelPackagePrice = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/travel-packages/${data.parent_id}/prices/${data.child_id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const postTravelPackagePrice = (token, data) => {
    return apiWrapper.post(`/travel-packages/${data.id}/prices`, data.form, headerWithToken(`JWT ${token}`))
  }

  const switchTravelPackagePrice = (token, { parent_id, child_id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/travel-packages/${parent_id}/prices/${child_id}`, form, headerWithToken(`JWT ${token}`))
  }

  // INSURANCE COLLECTIONS: TICKET PACKAGE
  const getTicketPackage = (token) => {
    return apiWrapper.get(`/ticket-packages`, null, headerWithToken(`JWT ${token}`))
  }

  const postTicketPackage = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/ticket-packages`, data, headerWithToken(`JWT ${token}`))
  }

  const putTicketPackage = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/ticket-packages/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const deleteTicketPackage = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/ticket-packages/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  // BANNER COLLECTIONS
  const getBanners = (token, filter) => {
    filter = { platform: null, q: null, iso_code: null, ...filter }
    return apiWrapper.get(`/banners`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createBanner = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/banners`, data, headerWithToken(`JWT ${token}`))
  }

  const deleteBanner = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/banners/${data.id}`, null, headerWithToken(`JWT ${token}`))
  }

  const updateBanner = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/banners/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  // MARKUP COLLECTIONS
  const getMarkup = (token, filter = { q: '', page: 1, sort: 'value' }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/markup-prices`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createMarkup = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/markup-prices`, data, headerWithToken(`JWT ${token}`))
  }

  const updateMarkup = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/markup-prices/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const deleteMarkup = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/markup-prices/${data.id}`, null, headerWithToken(`JWT ${token}`))
  }

  // MARKUP COLLECTIONS - INDIVIDUAL PACKAGE
  const markupItemCreate = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/markup-prices/items`, data.form, headerWithToken(`JWT ${token}`))
  }

  const markupItemUpdate = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/markup-prices/items/${data.uuid}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const markupItemDelete = (token, data) => {
    return apiWrapper.delete(`/markup-prices/items/${data.uuid}`, null, headerWithToken(`JWT ${token}`))
  }

  const markupItemGet = (token, data) => {
    return apiWrapper.get(`/markup-prices/${data.id}`, null, headerWithToken(`JWT ${token}`))
  }

  // EXCHANGE RATE COLLECTIONS
  const getExchangeRate = (token) => {
    return apiWrapper.get(`/exchange-rates`, null, headerWithToken(`JWT ${token}`))
  }

  const getSingleExchangeRate = (token, data) => {
    return apiWrapper.get(`/exchange-rates/${data.id}`, null, headerWithToken(`JWT ${token}`))
  }

  const updateExchangeRate = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/exchange-rates/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  // ORDERS COLLECTIONS
  const getOrder = (token, filter) => {
    filter = { page: 1, limit: 10, from: null, type: 'all', inventory_type: '', until: null, q: '', status: '', order_status: null, payment_method: '', transaction_amount_from: '', transaction_amount_to: '', ...filter }
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/orders`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getOrderSingle = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/orders/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const getChatbotOrder = (token, filter) => {
    return apiWrapper.get('/chatbot/orders', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const postChatbotOrder = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/chatbot/orders/${uuid}/confirm`, form, headerWithToken(`JWT ${token}`))
  }

  const getOrderDetailList = (token, filter) => {
    filter = { page: 1, limit: 10, created_from: null, created_until: null, type: '', status: '', ...filter }
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/orders`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const confirmOrder = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/orders/${data.id}/confirm`, data.form, headerWithToken(`JWT ${token}`))
  }

  const updateOrder = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/orders/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const updateCheckoutOrder = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/orders/`, data.form, headerWithToken(`JWT ${token}`))
  }

  const updateOrderAmount = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/orders/${data.id}/amount`, data.form, headerWithToken(`JWT ${token}`))
  }

  const getTransferHistory = (token, filter = { page: 1, limit: 10 }) => {
    return apiWrapper.get(`/transfer-histories`, filter, headerWithToken(`JWT ${token}`))
  }

  const getTransferHistoryMatches = (token) => {
    return apiWrapper.get(`/transfer-histories/matches`, null, headerWithToken(`JWT ${token}`))
  }

  const updateTransferHistory = (token, data = { id: null, form: {} }) => {
    return apiWrapper.put(`/transfer-histories/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const transferHistoryListSpreadsheet = (token, filter) => {
    filter = { from: null, until: null, status: null, ...filter }
    let params = []
    if (filter.from) {
      params.push('from=' + filter.from)
    }
    if (filter.until) {
      params.push('until=' + filter.until)
    }
    if (filter.status) {
      params.push('status=' + filter.status)
    }
    return axios({
      method: 'get',
      url: apiURL + '/transfer-histories/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `transfer_history-${moment().format('YMMDDhhmmss')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success getting transfer history list',
        status: true
      }
    })
  }

  const orderListSpreadsheet = (token, filter) => {
    filter = { from: null, until: null, code: '', status: '', order_status: null, payment_method: '', ...filter }
    let params = []
    if (filter.from) {
      params.push('from=' + filter.from)
    }
    if (filter.until) {
      params.push('until=' + filter.until)
    }
    if (filter.status) {
      params.push('status=' + filter.status)
    }
    if (filter.code) {
      params.push('code=' + filter.code)
    }
    if (filter.order_status) {
      params.push('order_status=' + filter.order_status)
    }
    if (filter.payment_method) {
      params.push('payment_method=' + filter.payment_method)
    }
    return axios({
      method: 'get',
      url: apiURL + '/orders/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `order_list-${moment().format('YMMDDhhmmss')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success getting order list',
        status: true
      }
    })
  }

  const orderDetailListSpreadsheet = (token, filter) => {
    filter = { created_from: null, created_until: null, code: '', status: '', type: 'modem', ...filter }
    let params = []
    if (filter.created_from) {
      params.push('created_from=' + filter.created_from)
    }
    if (filter.created_until) {
      params.push('created_until=' + filter.created_until)
    }
    if (filter.code) {
      params.push('code=' + filter.code)
    }
    if (filter.status) {
      params.push('status=' + filter.status)
    }
    if (filter.type) {
      params.push('type=' + filter.type)
    }
    return axios({
      method: 'get',
      url: apiURL + '/order-details/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `order_detail_list-${moment().format('YMMDDhhmmss')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success getting order detail list',
        status: true
      }
    })
  }

  const downloadOrderInvoice = (token, data) => {
    const { id, order_number } = data
    return axios({
      method: 'get',
      url: apiURL + '/orders/' + id + '/invoice',
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/pdf' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `INVOICE-${order_number}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success download invoice',
        status: true
      }
    })
  }

  const resendOrderVoucher = (token, data) => {
    const { id } = data
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`hooks/bookings/resend-voucher/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const getOrderReport = (token, filter) => {
    filter = {
      page: 1,
      limit: 10,
      from: null,
      until: null,
      type: 'modem',
      ...filter
    }
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/orders/report`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getOrderReportSpreadsheet = (token, filter) => {
    // filter = { ...filter }
    const { from, until, type } = filter
    let params = []
    params.push('from=' + from)
    params.push('until=' + until)
    params.push('type=' + type)
    return axios({
      method: 'get',
      url: apiURL + '/orders/report/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `travypass_order_report_${type}_${moment().format('YMMDDhhmmss')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return {
        ok: true,
        data: 'SUCCESS',
        message: 'Success download travy order report',
        status: true
      }
    })
  }

  const refundDana = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/payment/${data.uuid}/refund-dana`, ObjectCleaner(data.form), headerWithToken(`JWT ${token}`))
  }

  // USERS COLLECTIONS
  const getUser = (token, filter) => {
    const initFilter = {
      page: 1,
      limit: 10,
      q: '',
      registration_from: null,
      registration_until: null
      // only_admins: true
    }
    filter = {
      ...initFilter,
      ...filter
    }
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/users`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getUserCount = (token, filter = { from: '', until: '' }) => {
    return apiWrapper.get(`/users/stats/registration-rate`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createUser = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post('/auth/register', data, headerWithToken(`JWT ${token}`))
  }

  const updateUser = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/users/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  const deleteUser = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/users/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const downloadUserList = (token, data) => {
    data = { type: null, ...data }
    let params = []
    if (data.type) {
      params.push('type=' + data.type)
    }
    return axios({
      method: 'get',
      url: apiURL + '/users-list/spreadsheet?' + params.join('&'),
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `user_list_${data.type !== '' ? data.type : 'all'}_${moment().format('YMMDDhhmmss')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return {
        ok: true,
        data: 'success',
        message: 'Success download user list',
        status: true
      }
    })
  }

  // REVIEW
  const getReviewList = (token, filter = { page: 1, limit: 10, status: null }) => {
    return apiWrapper.get(`/reviews`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const approveReview = (token, id) => {
    return apiWrapper.put(`/reviews/${id}/verify`, {}, headerWithToken(`JWT ${token}`))
  }

  // DATABASE
  const dumpDatabase = (token) => {
    return axios({
      method: 'get',
      url: apiURL + '/dump-database',
      responseType: 'arraybuffer',
      headers: { authorization: `JWT ${token}` }
    }).then(function (response) {
      let blob = new Blob([response.data], { type: 'application/zip' })
      let link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `passpod_db_${moment().format('YMMDDhhmmss')}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return {
        ok: true,
        data: 'success',
        message: 'Success dump database',
        status: true
      }
    })
  }

  // ECOMMERCE CATEGORY COLLECTIONS
  const getCategory = (token, filter) => {
    filter = { page: 1, limit: 100, q: null, ...filter }
    return apiWrapper.get('/categories', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getSingleCategory = (token, id) => {
    return apiWrapper.get(`/categories/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const createCategory = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post('/categories', data, headerWithToken(`JWT ${token}`))
  }

  const updateCategory = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/categories/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE VARIAN-SIZES COLLECTIONS
  const getSize = (token, filter) => {
    filter = { page: 1, limit: 100, q: null, ...filter }
    return apiWrapper.get('/varian-sizes', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getAllSize = (token, filter) => {
    return apiWrapper.get(`/varian-sizes`, null, headerWithToken(`JWT ${token}`))
  }

  const createSize = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/varian-sizes`, data, headerWithToken(`JWT ${token}`))
  }

  const updateSize = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/varian-sizes/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteSize = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/varian-sizes/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE VARIAN-COLOURS COLLECTIONS
  const getColour = (token, filter) => {
    filter = { page: 1, limit: 100, q: null, ...filter }
    return apiWrapper.get('/varian-colours', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getAllColour = (token, filter) => {
    return apiWrapper.get('/varian-colours', null, headerWithToken(`JWT ${token}`))
  }

  const createColour = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/varian-colours`, data, headerWithToken(`JWT ${token}`))
  }

  const updateColour = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/varian-colours/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteColour = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/varian-colours/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE BRAND COLLECTIONS
  const getBrand = (token, filter) => {
    filter = { page: 1, limit: 100, q: null, ...filter }
    return apiWrapper.get('/brands', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createBrand = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/brands`, data, headerWithToken(`JWT ${token}`))
  }

  const updateBrand = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/brands/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteBrand = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/brands/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const getSingleBrand = (token, id) => {
    return apiWrapper.get(`/brands/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const createCategoryBrand = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/category-brands`, data, headerWithToken(`JWT ${token}`))
  }

  const updateCategoryBrand = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/category-brands/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteCategoryBrand = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/category-brands/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE WAREHOUSE COLLECTIONS
  const getWarehouse = (token, filter) => {
    filter = { page: 1, limit: 100, q: null, ...filter }
    return apiWrapper.get('/warehouses', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createWarehouse = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/warehouses`, data, headerWithToken(`JWT ${token}`))
  }

  const updateWarehouse = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/warehouses/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteWarehouse = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/warehouses/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE SUPPLIER COLLECTIONS
  const getSupplier = (token, filter) => {
    filter = { q: null, limit: 100, ...filter }
    return apiWrapper.get('/suppliers', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createSupplier = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/suppliers`, data, headerWithToken(`JWT ${token}`))
  }

  const updateSupplier = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/suppliers/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteSupplier = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/suppliers/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // Location API
  const getProvince = (token, filter) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get('/addresses/provinces')
  }

  const getCities = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get('/addresses/cities/', { province_id: id })
  }

  const getDistricts = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get('/addresses/districts/', { parent_id: id })
  }
  // ECOMMERCE INVENTORY COLLECTIONS
  const getInventory = (token, filter) => {
    filter = { page: 1, limit: 10, q: null, ...filter }
    return apiWrapper.get('/inventories', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const createInventory = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/inventories`, data, headerWithToken(`JWT ${token}`))
  }

  const updateInventory = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/inventories/${id}`, form, headerWithToken(`JWT ${token}`))
  }

  const updateInventoryImage = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/inventories/${id}/images`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteInventory = (token, id) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/inventories/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE PRODUCT COLLECTIONS
  const getEcommerceProduct = (token, filter) => {
    filter = { page: 1, limit: 10, q: null, ...filter }
    return apiWrapper.get('/inventories', ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const viewEcommerceProduct = (token, { inventoryID, ecommerceProductID }) => {
    return apiWrapper.get(`/inventories/${inventoryID}/items/${ecommerceProductID}`, null, headerWithToken(`JWT ${token}`))
  }

  const createEcommerceProduct = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/inventories/${id}/items`, form, headerWithToken(`JWT ${token}`))
  }

  const createEcommerceProductStock = (token, { inventoryID, ecommerceProductID, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/inventories/${inventoryID}/items/${ecommerceProductID}/stocks`, form, headerWithToken(`JWT ${token}`))
  }

  const updateEcommerceProduct = (token, { inventoryID, ecommerceProductID, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/inventories/${inventoryID}/items/${ecommerceProductID}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteEcommerceProduct = (token, { inventoryID, ecommerceProductID }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/inventories/${inventoryID}/items/${ecommerceProductID}`, null, headerWithToken(`JWT ${token}`))
  }

  // ECOMMERCE ORDER COLLECTIONS
  const getEcommerceOrder = (token, filter) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`order-products`, ObjectCleaner(filter), headerWithToken(`JWT ${token}`))
  }

  const getEcommerceOrderShipping = (token, { uuid }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/order-products/${uuid}/shipping`, null, headerWithToken(`JWT ${token}`))
  }

  const postEcommerceOrderShipping = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/order-products/${uuid}/shipping`, form, headerWithToken(`JWT ${token}`))
  }

  const putEcommerceOrderShipping = (token, { uuid, shippingUUID, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/order-products/${uuid}/shipping/${shippingUUID}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteEcommerceOrderShipping = (token, { uuid, shippingUUID }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/order-products/${uuid}/shipping/${shippingUUID}`, null, headerWithToken(`JWT ${token}`))
  }

  // SECTION REQUESTS
  const getSection = (token) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/sections`, null, headerWithToken(`JWT ${token}`))
  }

  const postSection = (token, { form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/sections`, form, headerWithToken(`JWT ${token}`))
  }

  const putSection = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/sections/${uuid}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteSection = (token, { uuid }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/sections/${uuid}`, null, headerWithToken(`JWT ${token}`))
  }

  const getSubSection = (token, { uuid }) => {} // will eventually replace props-based detail modal

  const postSubSection = (token, { form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/sub-sections/`, form, headerWithToken(`JWT ${token}`))
  }

  const putSubSection = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/sub-sections/${uuid}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteSubSection = (token, { uuid }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/sub-sections/${uuid}`, null, headerWithToken(`JWT ${token}`))
  }

  // ATTRACTION PROMO REQUESTS
  const getAttractionPromo = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`attraction-promotions`, data, headerWithToken(`JWT ${token}`))
  }

  const postAttractionPromo = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`attraction-promotions`, data, headerWithToken(`JWT ${token}`))
  }

  const putAttractionPromo = (token, { uuid, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`attraction-promotions/${uuid}`, form, headerWithToken(`JWT ${token}`))
  }

  const deleteAttractionPromo = (token, uuid) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`attraction-promotions/${uuid}`, null, headerWithToken(`JWT ${token}`))
  }

  // FAILED TRANSACTION REQUESTS
  const getFailedTransaction = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get('/failed-transactions', data, headerWithToken(`JWT ${token}`))
  }

  const triggerFailedTransaction = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get('/cron?key=resend-transaction', null, headerWithToken(`JWT ${token}`))
  }

  // CUSTOM REQUESTS
  const getProductCustom = (token) => {
    return apiWrapper.get(`/products`, null, headerWithToken(`JWT ${token}`))
  }

  const getBannerCustom = (token) => {
    return apiWrapper.get(`/banners`, null, headerWithToken(`JWT ${token}`))
  }

  const getInventoryCustom = (token) => {
    return apiWrapper.get('/inventories', null, headerWithToken(`JWT ${token}`))
  }

  // SCHEDULER REQUESTS
  const getScheduler = (token, filter) => {
    return apiWrapper.get(`/schedulers`, filter, headerWithToken(`JWT ${token}`))
  }

  const getSchedulerDetail = (token, { id }) => {
    return apiWrapper.get(`/schedulers/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  const postScheduler = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.post(`/schedulers`, ObjectCleaner(data), headerWithToken(`JWT ${token}`))
  }

  const putScheduler = (token, { id, form }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/schedulers/${id}`, ObjectCleaner(form), headerWithToken(`JWT ${token}`))
  }

  const deleteScheduler = (token, { id }) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.delete(`/schedulers/${id}`, null, headerWithToken(`JWT ${token}`))
  }

  // CRON-REPORT REQUESTS
  const getCronReport = (token, filter) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/cron/report`, filter, headerWithToken(`JWT ${token}`))
  }

  const getCronReportItem = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/cron/report/${data.id}`, data.filter, headerWithToken(`JWT ${token}`))
  }

  const disableAttraction = (token, body) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/cron/disable-attraction`, body, headerWithToken(`JWT ${token}`))
  }

  const enableAttraction = (token, body) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/cron/enable-attraction`, body, headerWithToken(`JWT ${token}`))
  }

  const getWhitelist = (token) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/whitelisted-attraction/`, null, headerWithToken(`JWT ${token}`))
  }

  const putWhitelist = (token, body) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/whitelisted-attraction?process=update`, body, headerWithToken(`JWT ${token}`))
  }

  const deleteWhitelist = (token, body) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/whitelisted-attraction?process=delete`, body, headerWithToken(`JWT ${token}`))
  }

  const getPromoSection = (token) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.get(`/sections/promo`, null, headerWithToken(`JWT ${token}`))
  }

  const updatePromoSection = (token, data) => {
    apiWrapper.setHeader('Content-Type', 'application/json')
    return apiWrapper.put(`/sections/promo/${data.id}`, data.form, headerWithToken(`JWT ${token}`))
  }

  // ------
  // STEP 3
  // ------
  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  // methods in step 2.
  //
  // Notice we're not returning back the `api` created in step 1?  That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    // a list of the API functions from step 2
    getUserData,
    getDatawithToken,
    postData,
    login,
    checkLogin,

    getCurrencies,
    getLanguages,
    getCountries,
    getPresetOrder,
    updatePresetOrder,

    getBranch,
    updateBranch,

    getShipping,
    updateShipping,

    getDestination,
    createDestination,
    deleteDestination,
    updateDestination,
    addAttractionToDestination,
    deleteAttractionFromDestination,
    getContinent,

    addPlaceToDestination,
    deletePlaceFromDestination,

    getPlacesV2,
    getPlacesByDestination,
    searchPlaces,

    getAttractionByDestination,
    getAttractionDetail,
    getPlaceDetail,
    updateAttractionDetail,
    clearAttractionCache,
    downloadAttractionData,
    searchAttraction,

    getPopularAttraction,
    savePopularAttraction,
    deletePopularAttraction,
    editPopularAttraction,

    getAllCoupon,
    getCouponDetail,
    createNewCoupon,
    updateCoupon,
    updateBusiness,
    deleteCoupon,
    deleteBusiness,
    getAllCouponTag,
    generateCoupon,
    couponSpreadsheet,
    historySpreadsheet,
    getCouponItem,
    postCouponItem,
    postCouponItemTag,
    deleteCouponItem,
    deleteCouponItemTag,
    getCouponProvider,
    postCouponProvider,
    putCouponProvider,
    deleteCouponProvider,
    getCouponPreset,
    postCouponPreset,
    putCouponPreset,
    deleteCouponPreset,
    getAffiliates,
    affiliateTransfer,

    getProduct,
    createProduct,
    deleteProduct,
    updateProduct,
    getProductCountry,
    postProductCountry,
    deleteProductCountry,
    getProductCampaign,
    postProductCampaign,
    putProductCampaign,
    deleteProductCampaign,
    getProductVariant,
    postProductVariant,
    putProductVariant,
    deleteProductVariant,
    getProductVariantItem,
    postProductVariantItem,
    deleteProductVariantItem,

    getAllEsims,
    updateEsimData,
    updateEsimCountry,
    updateEsimVariant,

    getAllEvents,
    // getEvents,
    getEventsCategories,
    getAllPopularEvents,
    setPopularEvent,
    resetEventDescription,
    updateEventDescription,

    getTravelPackage,
    viewTravelPackage,
    postTravelPackage,
    postTravelPackagePrice,
    putTravelPackage,
    putTravelPackagePrice,
    deleteTravelPackage,
    switchTravelPackagePrice,

    getTicketPackage,
    postTicketPackage,
    putTicketPackage,
    deleteTicketPackage,

    getBanners,
    createBanner,
    deleteBanner,
    updateBanner,

    getMarkup,
    createMarkup,
    updateMarkup,
    deleteMarkup,
    markupItemCreate,
    markupItemUpdate,
    markupItemDelete,
    markupItemGet,

    getExchangeRate,
    getSingleExchangeRate,
    updateExchangeRate,

    getUser,
    getUserCount,
    createUser,
    updateUser,
    deleteUser,
    downloadUserList,

    getOrder,
    getOrderSingle,
    getChatbotOrder,
    postChatbotOrder,
    getOrderDetailList,
    confirmOrder,
    getTransferHistory,
    getTransferHistoryMatches,
    updateOrder,
    updateCheckoutOrder,
    updateOrderAmount,
    updateTransferHistory,
    orderListSpreadsheet,
    orderDetailListSpreadsheet,
    transferHistoryListSpreadsheet,
    downloadOrderInvoice,
    resendOrderVoucher,
    getOrderReport,
    getOrderReportSpreadsheet,
    refundDana,

    uploadFiles,

    getReviewList,
    approveReview,

    dumpDatabase,

    getCategory,
    getSingleCategory,
    createCategory,
    updateCategory,

    getColour,
    getAllColour,
    createColour,
    updateColour,
    deleteColour,

    getSize,
    getAllSize,
    createSize,
    updateSize,
    deleteSize,

    getBrand,
    createBrand,
    updateBrand,
    deleteBrand,
    getSingleBrand,
    createCategoryBrand,
    updateCategoryBrand,
    deleteCategoryBrand,

    getWarehouse,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,

    getSupplier,
    createSupplier,
    updateSupplier,
    deleteSupplier,

    getProvince,
    getCities,
    getDistricts,

    getInventory,
    createInventory,
    updateInventory,
    updateInventoryImage,
    deleteInventory,

    getEcommerceProduct,
    viewEcommerceProduct,
    createEcommerceProduct,
    createEcommerceProductStock,
    updateEcommerceProduct,
    deleteEcommerceProduct,

    getEcommerceOrder,
    getEcommerceOrderShipping,
    postEcommerceOrderShipping,
    putEcommerceOrderShipping,
    deleteEcommerceOrderShipping,

    getSection,
    postSection,
    putSection,
    deleteSection,
    getSubSection,
    postSubSection,
    putSubSection,
    deleteSubSection,

    getAttractionPromo,
    postAttractionPromo,
    putAttractionPromo,
    deleteAttractionPromo,

    getFailedTransaction,
    triggerFailedTransaction,

    getProductCustom,
    getBannerCustom,
    getInventoryCustom,

    getScheduler,
    getSchedulerDetail,
    postScheduler,
    putScheduler,
    deleteScheduler,

    getCronReport,
    getCronReportItem,
    disableAttraction,
    enableAttraction,
    getWhitelist,
    putWhitelist,
    deleteWhitelist,

    getPromoSection,
    updatePromoSection
  }
}

// let's return back our create method as the default.
export default { create }



// WEBPACK FOOTER //
// ./src/Services/Api.js